import styles from 'legacy/common/components/form/Form.module.scss';

import React, { useState } from 'react';
import Link from 'next/link';
import Router, { useRouter } from 'next/router';
import { useTranslation } from 'react-i18next';
import { useFlags } from 'launchdarkly-react-client-sdk';

import {
  useConfirmLoginVerificationSessionCommand,
  useCreateLoginVerificationSessionCommand,
  useUpdateLoginVerificationSessionCommand
} from 'legacy/auth/hooks';

import { handleLogin } from 'legacy/auth/helpers';
import { FLAGS } from 'legacy/common/constants/launchdarkly';

import { VerificationSessionDto } from 'legacy/auth/types';

import useParams from 'legacy/common/hooks/useParams';

import Button from 'legacy/common/components/button/Button';
import FormWrapper from 'legacy/auth/components/FormWrapper';
import LoginForm from 'legacy/auth/components/login/LoginForm';
import Wrapper from 'legacy/common/components/layout/BackgroundWrapper';
import LoginConfirmation from 'legacy/auth/components/login/LoginConfirmation';
import LoginWithPasswordForm from 'legacy/auth/components/login/LoginWithPasswordForm';
import LoginMethodsList from 'legacy/auth/components/login/LoginMethodsList';

const handleLegacyEmailLogin = (email: string, from: string) => {
  Router.push(
    {
      pathname: '/login',
      query: {
        type: 'email',
        email,
        from
      }
    },
    '/login'
  );
};

const handleLegacyPhoneLogin = (phone: string, from: string) => {
  Router.push(
    {
      pathname: '/login',
      query: {
        type: 'phone',
        phone,
        from
      }
    },
    '/login'
  );
};

const SignUpButton = () => {
  const { t } = useTranslation('auth');
  const { query } = useRouter();

  return (
    <>
      <div className={styles.separator}>
        <span />
        <div>{t('common.or')}</div>
        <span />
      </div>
      <div className="mt-6">
        <Link
          href={{
            pathname: '/register',
            query
          }}
          legacyBehavior
        >
          <Button data-test="sign-up">{t('buttons.signUp')}</Button>
        </Link>
      </div>
    </>
  );
};

const Login = () => {
  const params = useParams<{ from: string }>();
  const flags = useFlags();

  const [identifier, setIdentifier] = useState('');
  const [showMoreOptions, setShowMoreOptions] = useState(false);
  const [showMethodsList, setShowMethodsList] = useState(false);
  const [confirmationError, setConfirmationError] = useState<
    string | undefined
  >();

  const [verificationSession, setVerificationSession] =
    useState<VerificationSessionDto>();
  const createLoginVerificationSessionCommand =
    useCreateLoginVerificationSessionCommand();
  const updateLoginVerificationSessionCommand =
    useUpdateLoginVerificationSessionCommand();
  const confirmLoginVerificationSessionCommand =
    useConfirmLoginVerificationSessionCommand();

  const handleInitialLogin = (values: {
    email?: string;
    phoneNumber?: string;
    callingCode: string;
    countryCode: string;
  }) => {
    const emailValue = values.email;
    const phoneValue = values.phoneNumber;

    // Use the current view (showMoreOptions) to determine which method to use
    // showMoreOptions: false = email view, true = phone view
    if (!showMoreOptions && emailValue) {
      setIdentifier(emailValue);

      // Determine if OTP should be required based on the feature flag
      const isOtpEnabled = flags[FLAGS.ENABLE_OTP_EMAIL_LOGIN];

      createLoginVerificationSessionCommand.mutate(
        {
          email: emailValue,
          // Only set requireOtp for email logins, based on the feature flag
          requireOtp: isOtpEnabled
        },
        {
          onSuccess: (data) => {
            const session = data as VerificationSessionDto;

            if ('isLegacyAuthUser' in data && data.isLegacyAuthUser) {
              handleLegacyEmailLogin(emailValue, params.from);
              return;
            }

            setVerificationSession({
              ...session,
              deliveryMethod: 'email'
            });
          }
        }
      );
      return;
    }

    if (showMoreOptions && phoneValue) {
      setIdentifier(phoneValue);

      createLoginVerificationSessionCommand.mutate(
        {
          phoneNumber: phoneValue,
          countryCode: values.callingCode
        },
        {
          onSuccess: (data) => {
            if ('isLegacyAuthUser' in data && data.isLegacyAuthUser) {
              handleLegacyPhoneLogin(phoneValue, params.from);
              return;
            }

            setVerificationSession({
              ...(data as VerificationSessionDto),
              deliveryMethod: 'sms'
            });
          }
        }
      );
    }
  };

  const handlePasswordLogin = (values: { password: string }) => {
    if (!verificationSession?.verificationSessionId) {
      // This should technically never happen, but just in case
      return;
    }

    // Clear any previous confirmation errors when submitting password
    setConfirmationError(undefined);

    updateLoginVerificationSessionCommand.mutate(
      {
        verificationSessionId: verificationSession.verificationSessionId,
        password: values.password
      },
      {
        onSuccess: (data) => {
          const isOtpEnabled = flags[FLAGS.ENABLE_OTP_EMAIL_LOGIN];

          // If OTP is disabled for EMAIL logins only, skip OTP step and log in directly
          // Phone logins always require OTP verification regardless of this flag
          if (!isOtpEnabled && verificationSession.deliveryMethod === 'email') {
            // Clear any previous confirmation errors
            setConfirmationError(undefined);

            confirmLoginVerificationSessionCommand.mutate(
              {
                verificationSessionId: verificationSession.verificationSessionId
              },
              {
                onSuccess: (loginData) => {
                  handleLogin(loginData, { from: params.from });
                },
                onError: (error) => {
                  // Store the error so it can be displayed in the password form
                  setConfirmationError(error.message);
                }
              }
            );
            return;
          }

          // For phone logins or when email OTP is enabled, show OTP verification screen
          setVerificationSession({
            ...data,
            deliveryMethod: verificationSession.deliveryMethod
          });
        }
      }
    );
  };

  const handleConfirmLogin = (code: string) => {
    confirmLoginVerificationSessionCommand.mutate(
      {
        smsCode: code,
        verificationSessionId: verificationSession.verificationSessionId
      },
      {
        onSuccess: (data) => {
          handleLogin(data, { from: params.from });
        }
      }
    );
  };

  const onReset = () => {
    setVerificationSession(null);
    setShowMethodsList(false);
    setConfirmationError(undefined);

    createLoginVerificationSessionCommand.reset();
    updateLoginVerificationSessionCommand.reset();
    confirmLoginVerificationSessionCommand.reset();
  };

  const handleSelectMethod = (method: 'email' | 'phone') => {
    setShowMoreOptions(method === 'phone');
    setShowMethodsList(false);
  };

  if (
    (verificationSession &&
      !verificationSession.requiresPasswordVerification) ||
    verificationSession?.hasVerifiedPassword
  ) {
    return (
      <Wrapper>
        <LoginConfirmation
          onBackClick={onReset}
          verificationSession={verificationSession}
          onSubmit={handleConfirmLogin}
          isLoading={confirmLoginVerificationSessionCommand.isLoading}
          errorMessage={confirmLoginVerificationSessionCommand.error?.message}
        />
      </Wrapper>
    );
  }

  if (verificationSession?.requiresPasswordVerification) {
    return (
      <Wrapper>
        <FormWrapper>
          <LoginWithPasswordForm
            phoneOrEmail={identifier}
            onReset={onReset}
            onSubmit={handlePasswordLogin}
            isLoading={
              updateLoginVerificationSessionCommand.isLoading ||
              confirmLoginVerificationSessionCommand.isLoading
            }
            errorMessage={
              updateLoginVerificationSessionCommand.error?.message ||
              confirmationError
            }
          />
          <SignUpButton />
        </FormWrapper>
      </Wrapper>
    );
  }

  if (showMethodsList) {
    return (
      <Wrapper>
        <LoginMethodsList
          currentMethod={showMoreOptions ? 'phone' : 'email'}
          onSelectMethod={handleSelectMethod}
          onBack={() => setShowMethodsList(false)}
        />
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <FormWrapper>
        <LoginForm
          onSubmit={handleInitialLogin}
          isLoading={createLoginVerificationSessionCommand.isLoading}
          errorMessage={createLoginVerificationSessionCommand.error?.message}
          showMoreOptions={showMoreOptions}
          onShowMethodsList={() => setShowMethodsList(true)}
        />
        <SignUpButton />
      </FormWrapper>
    </Wrapper>
  );
};

export default Login;
